import { DefaultButton, IButtonStyles, IPanelStyles, IStackStyles, Label, mergeStyles, Panel, PanelType, ScrollablePane, ScrollbarVisibility, Stack } from "@fluentui/react";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../src/taskpane/components/_v2/redux/storeMain";
import { setContentManagerFilterSections, setContentManagerFilterSectionsReset } from "../../../src/taskpane/redux/modules/contentManagerDirectory";
import { toggleReload } from "../../../src/taskpane/redux/modules/reload";
import { LanguageContext } from "../../../utilities/LocalizationModule";
import { CMFileFilterPanelSection } from "./CMFileFilterPanelSection";
import Loader from "../../../src/taskpane/components/Loader";
import { IViewsDropdown } from "../../../src/taskpane/interfaces/IViewsDropdown";
import { setFilterSectionsFromFields } from "./FileFilterHelper";
import { useClientTypeContext } from "../../../src/taskpane/components/hooks/useClientTypeContext";
import { ClientTypeContext } from "../../../src/taskpane/components/helpers/dataSourceFieldHelper";
import { preselectFilterStore } from "../../Body/hooks/preselectFilterStore";
import { usePreselectStoreVersion } from "../../Body/hooks/usePreselectStoreVersion";

export const scrollStyle = mergeStyles({
  position: "relative",
  height: "calc(100vh - 140px)",
  marginTop: 5,
  marginBottom: 10
});

export const panelActionButtonStyle: Partial<IButtonStyles> = {
  root: {
    marginTop: 20,
  }
}

export const panelStyles: Partial<IPanelStyles> = {
  main: {
    overflow: "hidden"
  },
  content: {
    paddingLeft: 5,
    paddingRight: 5,
    paddingBottom: 0
  }
}

const noFiltersStackStyles: Partial<IStackStyles> = {
  root: { width: "100%", height: "100%", margin: 0, padding: 0, overflow: "auto" }
};

const stackTokens = { childrenGap: 25 };

export interface ICMFileFilterPanelProps {
  closePanel?: () => void;
  panelIsOpen: boolean;
  isInOneDrive: boolean;
  serverRelPath: string;
  selectedView?: IViewsDropdown;
}

export const CMFileFilterPanel = ({ closePanel, panelIsOpen, isInOneDrive, serverRelPath, selectedView }: ICMFileFilterPanelProps) => {
  const languageStrings = React.useContext(LanguageContext);

  const filterFields = useSelector((state: RootState) => state.contentManagerDirectory.filterFields);
  const filterSections = useSelector((state: RootState) => state.contentManagerDirectory.filterSections);
  const preselectReconcilePending = useSelector((state: RootState) => state.contentManagerDirectory.preselectReconcilePending);
  const oneDriveRootDetails = useSelector((state: RootState) => state.workingDirectories?.oneDriveRootDetails);
  const currentDirectory = useSelector((state: RootState) => state.workingDirectories?.workingDirectories?.length > 0 ? state.workingDirectories.workingDirectories[state.workingDirectories.workingDirectories.length - 1] : null);
  const userAccount = useSelector((state: RootState) => state.user.account);
  const datasourceItems = useSelector((state: RootState) => state.tenantInfo.datasources);
  const hideTitleFieldFilter = useSelector((state: RootState) => state.tenantInfo?.hideTitleFieldFilter);
  const clientTypes = useClientTypeContext(currentDirectory?.siteId);
  const [isLoadingFilters, setIsLoadingFilters] = React.useState<boolean>(false);

  const dispatch = useDispatch();

  const [filterSelected, setFilterSelected] = React.useState<boolean>(false);
  const [hasFilters, setHasFilters] = React.useState(true);

  const buildInFlightRef = React.useRef<boolean>(false);

  // The reconcile owns the section build while a deep link targeting THIS
  // directory is unconsumed or its reconcile is pending: it passes
  // keepFieldKeys and applies the querystring selections, and a competing
  // un-kept build here can pre-empt the deep link entirely (the reconcile's
  // eligibility gate requires an empty section set on the no-fast-path route)
  // or land an all-unselected section set over the reconciled one. Directory-
  // scoped on purpose — an unconsumed deep link bound elsewhere must NOT block
  // this directory's build. Two reactive edges re-open the gate: the pending
  // flag (redux) when a reconcile run settles, and the store version
  // (subscription below) when the deep link is consumed OUTSIDE a reconcile —
  // e.g. the grid giving up on the target's fetch. Without the second edge a
  // consumption with no accompanying redux change left the Loader up forever.
  const preselectStoreVersion = usePreselectStoreVersion();
  const reconcileOwnsBuild = preselectReconcilePending || preselectFilterStore.hasPendingDeepLink(currentDirectory?.id);

  // The context the last build here ran under. A verdict that settles AFTER a build would
  // otherwise latch the pre-resolution section set (split shapes, restricted fields) until a
  // directory change. Rebuilding is only safe while nothing is selected - a rebuild discards
  // selections - so a settled verdict over a touched section set deliberately keeps the latch.
  const builtWithClientTypesRef = React.useRef<ClientTypeContext | undefined>(undefined);
  React.useEffect(() => {
    const nothingSelected = !filterSections.some(section =>
      section.daysOffset || section.daysOffset === 0 || section.items?.some(item => item.selected));
    const staleContextRebuild = filterSections.length > 0
      && builtWithClientTypesRef.current !== undefined
      && builtWithClientTypesRef.current !== clientTypes
      && nothingSelected;
    if ((filterSections.length > 0 && !staleContextRebuild) || reconcileOwnsBuild || buildInFlightRef.current) {
      return;
    }
    builtWithClientTypesRef.current = clientTypes;
    buildInFlightRef.current = true;
    setIsLoadingFilters(true);
    (async () => {
      try {
        const displayFilterSections = await setFilterSectionsFromFields(isInOneDrive,
          oneDriveRootDetails,
          currentDirectory,
          userAccount,
          filterFields,
          hideTitleFieldFilter,
          serverRelPath,
          languageStrings.Me,
          selectedView,
          datasourceItems,
          undefined,
          clientTypes
        );

        dispatch(setContentManagerFilterSections(displayFilterSections));
        setHasFilters(displayFilterSections.some(section => section.items?.length > 0));
      } catch (error) {
        // Ignore
      }
      setIsLoadingFilters(false);
      buildInFlightRef.current = false;
    })();
    // preselectStoreVersion: an external consumption (grid give-up) must re-run
    // this build — the render alone re-evaluates the gate but would leave the
    // section set empty (the blank-body trap). filterFields.length: a build
    // that ran while the directory's fields had not landed yet (panel open
    // across a navigation) latches an empty "no filters" result; the fields
    // arriving must re-fire it or the panel shows the wrong message until
    // closed and reopened. clientTypes: a verdict settling after a build must
    // re-fire it (selection-safe — see staleContextRebuild above).
  }, [filterSections.length, preselectReconcilePending, preselectStoreVersion, filterFields.length, clientTypes]);

  React.useEffect(() => {
    setFilterSelected(hasFilters && (filterSections?.some(section => section.daysOffset || section.daysOffset === 0) || filterSections?.some(section => section.items.some(item => item.selected === true))));
  }, [hasFilters, filterSections]);

  return (
    <Panel
      headerText={languageStrings.Filter}
      isOpen={panelIsOpen}
      onDismiss={() => {
        closePanel();
      }}
      isLightDismiss={true}
      closeButtonAriaLabel="Close"
      type={PanelType.custom}
      customWidth={'350px'}
      styles={panelStyles}
    >
      <div className={scrollStyle} >
        <ScrollablePane scrollbarVisibility={ScrollbarVisibility.auto}>
          <div className="xb-content" style={{ marginLeft: 20 }}>
            {/* While a pre-select reconcile owns the build (in flight, or a deep
                link targeting this directory is still unconsumed) the panel
                holds its loader instead of showing the transient synthesized-
                only sections — or, worse, a silently blank body. */}
            {(isLoadingFilters || reconcileOwnsBuild) && <Loader label={languageStrings.LoadingFilters} />}
            {!reconcileOwnsBuild && !hasFilters && <Stack verticalAlign="center" horizontalAlign="center" styles={noFiltersStackStyles}>
              <Stack.Item align="center">
                <Label disabled>{languageStrings.NoFiltersAvailable}</Label>
              </Stack.Item>
            </Stack>}
            {!reconcileOwnsBuild && hasFilters && <Stack tokens={stackTokens}>
              {filterSections?.length > 0
                && filterSections
                  .map((section, index) => {
                    return (
                      <Stack.Item key={index}>
                        <CMFileFilterPanelSection
                          filterSections={filterSections}
                          filterSection={section}
                          isInOneDrive={isInOneDrive}
                          serverRelPath={serverRelPath}
                          oneDriveRootDetails={oneDriveRootDetails}
                          currentDirectory={currentDirectory}
                          isInContentManagerMode={true}
                        />
                      </Stack.Item>
                    )
                  })}
            </Stack>}
          </div>
        </ScrollablePane>
      </div>
      {filterSections?.length > 0 && hasFilters && <Stack horizontal horizontalAlign="end" tokens={{ childrenGap: 15 }} style={{ paddingRight: 10 }}>
        <DefaultButton
          styles={panelActionButtonStyle}
          iconProps={{ iconName: 'Refresh' }}
          disabled={!filterSelected}
          onClick={async () => {
            // Cancel an in-flight deep-link reconcile: its abandon gate checks
            // isConsumed(), so a late re-publish cannot resurrect the selections
            // this Reset just cleared. (The reducer drops the pending flag.)
            // Directory-scoped: a reconcile can only be in flight for THIS
            // directory (eligibility requires being the target), and an
            // unconsumed deep link bound elsewhere must survive a Reset here.
            preselectFilterStore.abandonPendingDeepLink(currentDirectory?.id);
            dispatch(setContentManagerFilterSectionsReset());
            dispatch(toggleReload());
          }}
          text={languageStrings.Reset}
        />
      </Stack>}
    </Panel>
  )
};